import {
  MONTH_NAMES,
  NUMBER_WORD_SOURCE,
  ORDINAL_WORD_SOURCE,
  SINGLE_DIGIT_WORD_SOURCE,
  getSingleDigitValue,
  hasDateCue,
  isModernYear,
  isScaleWord,
  normalizeDictatedDates,
  parseCardinal,
  parseSpokenDay,
  parseSpokenYear,
  readGroup,
  readGroups,
  toNumberTokens,
} from "./dictationDates";

export type SpokenDictationMode = "plain" | "formatted";

export interface NormalizeSpokenDictationOptions {
  /** `plain`: Deepgram sent unformatted words. `formatted`: Deepgram already punctuated and cased it. */
  mode: SpokenDictationMode;
  /** Note text before the cursor, so a date cue like "done on" from an earlier piece still counts. */
  textBefore?: string;
}

const NEWLINE_TOKEN = "__KNOWTEX_DICTATION_NEWLINE__";
export const DICTATION_PUNCTUATION_CHARACTERS = ".,!?;:%()[]{}\"'/\\-";
const PHONE_NUMBER_DIGIT_COUNT = 10;

const NUMBER_WORD_RUN_PATTERN = new RegExp(
  `\\b(?:${NUMBER_WORD_SOURCE})(?:[\\s-]+(?:${NUMBER_WORD_SOURCE}|and))*\\b`,
  "gi"
);
const PHONE_LABEL_SOURCE =
  "(?:phone|telephone)(?:\\s+number)?|cell(?:\\s+phone)?(?:\\s+number)?|mobile(?:\\s+number)?";
const PHONE_NUMBER_PATTERN = new RegExp(
  `\\b(${PHONE_LABEL_SOURCE})(\\s+is)?\\s+((?:${SINGLE_DIGIT_WORD_SOURCE})(?:[\\s-]+(?:${SINGLE_DIGIT_WORD_SOURCE}))+)\\b`,
  "gi"
);
const PHONE_NUMBER_CONTEXT_PATTERN = new RegExp(
  `\\b(?:${PHONE_LABEL_SOURCE})(?:\\s+is)?\\s+(\\d{1,${PHONE_NUMBER_DIGIT_COUNT - 1}})\\s*$`,
  "i"
);

// "period" stays a word in noun phrases like "last menstrual period" or "period of time".
const PERIOD_NOUN_CONTEXT_SOURCE = [
  "menstrual",
  "last",
  "first",
  "grace",
  "waiting",
  "incubation",
  "refractory",
  "postoperative",
  "postop",
  "post-op",
  "postpartum",
  "recovery",
  "trial",
  "washout",
  "window",
  "observation",
  "treatment",
  "exam",
  "examination",
  "study",
  "time",
  "follow-up",
  "induction",
  "maintenance",
  "dosing",
  "infusion",
  "healing",
  "symptom-free",
  "asymptomatic",
  "latent",
  "perioperative",
  "neonatal",
  "the",
  "a",
  "this",
  "that",
  "her",
  "his",
  "my",
  "your",
  "their",
  "our",
  "its",
  "next",
  "previous",
  "prior",
  "each",
  "every",
  "any",
  "no",
  "one",
  "same",
  "short",
  "long",
  "brief",
].join("|");
// The noun check runs in code instead of a lookbehind, which older Safari can't parse.
const PERIOD_COMMAND_PATTERN = /\bfull stop\b|\bperiod\b(?!\s+of\b)/gi;
const PERIOD_NOUN_BEFORE_PATTERN = new RegExp(`\\b(?:${PERIOD_NOUN_CONTEXT_SOURCE})[\\s-]+$`, "i");
const isPeriodCommandAt = (match: string, offset: number, whole: string): boolean =>
  !/^period$/i.test(match) || !PERIOD_NOUN_BEFORE_PATTERN.test(whole.slice(0, offset));

// "draw a new line" and "the new line of treatment" are phrases, not commands.
const NEWLINE_COMMAND_PATTERN = /\b(?:new\s+line|newline)\b(?!\s+of\b)/gi;
const NEWLINE_NOUN_BEFORE_PATTERN = /\b(?:a|an|the|this|that|another|any|no|one|each)\s+$/i;

const PERIOD_TOKEN = "__KNOWTEX_DICTATION_PERIOD__";
const COMMA_TOKEN = "__KNOWTEX_DICTATION_COMMA__";
type CommandReplacement = string | ((match: string, offset: number, whole: string) => string);
const PUNCTUATION_COMMANDS: Array<[RegExp, CommandReplacement]> = [
  [
    PERIOD_COMMAND_PATTERN,
    (match, offset, whole) =>
      isPeriodCommandAt(match, offset, whole) ? ` ${PERIOD_TOKEN} ` : match,
  ],
  // "comma separated values" is a phrase, not a command.
  [/\bcomma\b(?![\s-]+(?:separated|delimited)\b)/gi, ` ${COMMA_TOKEN} `],
  [
    NEWLINE_COMMAND_PATTERN,
    (match, offset, whole) =>
      NEWLINE_NOUN_BEFORE_PATTERN.test(whole.slice(0, offset)) ? match : ` ${NEWLINE_TOKEN} `,
  ],
];
// Smart Format adds its own punctuation around spoken commands ("stable period." or
// "stable. Period"); the spoken command wins.
const PUNCTUATION_NEXT_TO_COMMAND_PATTERN = new RegExp(
  `[.,;:!?]*\\s*(${PERIOD_TOKEN}|${COMMA_TOKEN}|${NEWLINE_TOKEN})\\s*[.,;:!?]*`,
  "g"
);
const LOWERCASE_AFTER_SPOKEN_PERIOD_PATTERN = new RegExp(`(${PERIOD_TOKEN}\\s+)([a-z])`, "g");
const OPEN_QUOTE_TOKEN = "__KNOWTEX_DICTATION_OPEN_QUOTE__";
const CLOSE_QUOTE_TOKEN = "__KNOWTEX_DICTATION_CLOSE_QUOTE__";
// Multi-word commands are safe everywhere. Close quotes become placeholders first so the
// "quote" in "end quote" is gone before a bare "quote" is checked for a later close quote.
const EXPLICIT_COMMANDS: Array<[RegExp, string]> = [
  [/\b(?:open|left)\s+(?:paren|parenthesis|parentheses|paranthesis)\b/gi, "("],
  [/\b(?:close|closed|right|end)\s+(?:paren|parenthesis|parentheses|paranthesis)\b/gi, ")"],
  [/\b(?:close|end)\s+quote\b|\bunquote\b/gi, ` ${CLOSE_QUOTE_TOKEN} `],
  [/\b(?:open|begin)\s+quote\b/gi, ` ${OPEN_QUOTE_TOKEN} `],
  [new RegExp(`\\bquote\\b(?=[^\\n]*?${CLOSE_QUOTE_TOKEN})`, "gi"), ` ${OPEN_QUOTE_TOKEN} `],
  [/\bforward\s+slash\b/gi, "/"],
];
// Quote placeholders outlive the generic `"…"` space cleanup, which can't tell an open quote
// from a close quote. Smart Format can also wrap them in commas ("states, quote, I feel fine,
// end quote."), so spacing and commas just inside the quotes are dropped.
const SPACING_INSIDE_OPEN_QUOTE_PATTERN = new RegExp(`(${OPEN_QUOTE_TOKEN})[ \\t,;:]*`, "g");
const SPACING_INSIDE_CLOSE_QUOTE_PATTERN = new RegExp(`[ \\t,;:]*(${CLOSE_QUOTE_TOKEN})`, "g");
const QUOTE_TOKEN_PATTERN = new RegExp(`${OPEN_QUOTE_TOKEN}|${CLOSE_QUOTE_TOKEN}`, "g");
// Single words like "dash", "slash" and "quote" are also ordinary words ("dash cam",
// "slash wound"), so they only count as commands when Deepgram sends unformatted words.
const BARE_COMMANDS: Array<[RegExp, string]> = [
  [/\b(?:paren|parenthesis|parentheses|paranthesis)\b/gi, "("],
  [/\b(?:double\s+quote|quote|quotation\s+mark)\b/gi, '"'],
  [/\b(?:dash|hyphen)\b/gi, "-"],
  [/\bslash\b/gi, "/"],
];

const toDigitString = (words: string): string =>
  words
    .split(/[\s-]+/)
    .map((word) => getSingleDigitValue(word))
    .join("");

const collapseRepeatedCommandOutput = (value: string): string => {
  // Two spoken new lines make a blank line between paragraphs.
  if (value.includes("\n") && value.replace(/\n/g, "").trim() === "") {
    return value.replace(/[^\n]/g, "").slice(0, 2);
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) return "";

  const characters = Array.from(trimmedValue).filter((character) => !/[ \t]/.test(character));
  if (
    characters.length > 1 &&
    characters.every((character) => character === characters[0]) &&
    DICTATION_PUNCTUATION_CHARACTERS.includes(characters[0])
  ) {
    return characters[0];
  }

  return value;
};

// Separate numbers are never added together ("nineteen eighty five" is 1985, not 104).
const convertNumberRun = (run: string, mode: SpokenDictationMode, hasCue: boolean): string => {
  const words = run.split(/[\s-]+/).filter(Boolean);
  const tokens = words.map((word) => word.toLowerCase()).filter((word) => word !== "and");

  if (tokens.some(isScaleWord)) {
    const value = parseCardinal(tokens);
    if (value !== null) return String(value);
  }

  const groups = readGroups(tokens);
  if (!groups) return run;
  if (groups.length === 1) return groups[0] > 9 ? String(groups[0]) : run;

  const year = hasCue && tokens.length === words.length ? parseSpokenYear(tokens) : null;
  if (year !== null && year >= 1800 && year <= 2099) return String(year);
  // Deepgram's Smart Format already writes real numbers as digits, so leftover runs are words.
  if (mode === "formatted") return run;

  const output: string[] = [];
  let index = 0;
  while (index < words.length) {
    if (words[index].toLowerCase() === "and") {
      output.push(words[index]);
      index += 1;
      continue;
    }
    const group = readGroup(words.slice(index).map((word) => word.toLowerCase()));
    if (!group) return run;
    const groupWords = words.slice(index, index + group.length);
    output.push(group.value > 9 ? String(group.value) : groupWords.join(" "));
    index += group.length;
  }
  return output.join(" ");
};

const normalizeSpokenNumbers = (
  value: string,
  mode: SpokenDictationMode,
  textBefore: string
): string =>
  value
    .replace(
      PHONE_NUMBER_PATTERN,
      (_match, label: string, isText: string | undefined, digitWords: string) =>
        `${label}${isText || ""} ${toDigitString(digitWords)}`
    )
    .replace(NUMBER_WORD_RUN_PATTERN, (run: string, offset: number, whole: string) =>
      convertNumberRun(run, mode, hasDateCue(whole, offset, textBefore))
    );

const MONTH_SOURCE = MONTH_NAMES.join("|");
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
// "may" and "march" are also everyday words ("may two", "may first try"), so they need a
// year or an unambiguous ordinal day to count as a date.
const AMBIGUOUS_MONTHS = new Set(["may", "march"]);
const AMBIGUOUS_MONTH_DAY_WORDS = new Set(["first", "second"]);
const MONTH_DATE_PATTERN = new RegExp(
  `\\b(${MONTH_SOURCE})((?:,?[\\s-]+(?:${ORDINAL_WORD_SOURCE}|${NUMBER_WORD_SOURCE}|and|oh))+)\\b`,
  "gi"
);
const TRAILING_MONTH_DATE_PATTERN = new RegExp(
  `\\b(?:${MONTH_SOURCE})[ \\t]+\\d{1,2}[ \\t]*$`,
  "i"
);
const LEADING_YEAR_RUN_PATTERN = new RegExp(
  `^(?:${NUMBER_WORD_SOURCE})(?:[\\s-]+(?:and[\\s-]+)?(?:${NUMBER_WORD_SOURCE}|oh))*\\b`,
  "i"
);
const DECIMAL_PATTERN = new RegExp(
  `\\b((?:${NUMBER_WORD_SOURCE})(?:[\\s-]+(?:and[\\s-]+)?(?:${NUMBER_WORD_SOURCE}))*)\\s+point\\s+((?:${SINGLE_DIGIT_WORD_SOURCE}|oh)(?:[\\s-]+(?:${SINGLE_DIGIT_WORD_SOURCE}|oh))*)\\b`,
  "gi"
);
const A1C_PATTERN = /\ba[\s-]*one[\s-]*c\b/gi;

const capitalizeMonth = (month: string): string =>
  `${month.charAt(0).toUpperCase()}${month.slice(1).toLowerCase()}`;

interface LeadingYear {
  year: number;
  length: number;
}

// Reads the longest run of leading words that is a year, so words after it stay as spoken.
// "and" only belongs to a year after a scale word ("two thousand and five"), not before a
// separate quantity ("twenty twenty six and two tablets").
const readLeadingYear = (words: string[]): LeadingYear | null => {
  const lowerWords = words.map((word) => word.toLowerCase());
  for (let length = lowerWords.length; length > 0; length -= 1) {
    const yearWords = lowerWords.slice(0, length);
    const hasStrayAnd = yearWords.some(
      (word, index) => word === "and" && (index === 0 || !isScaleWord(yearWords[index - 1]))
    );
    if (hasStrayAnd || yearWords[length - 1] === "and") continue;
    const year = parseSpokenYear(toNumberTokens(yearWords.join(" ")));
    if (year !== null && isModernYear(year)) return { year, length };
  }
  return null;
};

const joinWords = (...parts: string[]): string => parts.filter(Boolean).join(" ");

// "april twenty ninth twenty twenty six" -> "April 29, 2026". The month name is the cue.
const convertMonthDate = (match: string, month: string, tail: string): string => {
  const words = tail.split(/[\s,-]+/).filter(Boolean);
  const lowerWords = words.map((word) => word.toLowerCase());
  const monthName = capitalizeMonth(month);
  const isAmbiguousMonth = AMBIGUOUS_MONTHS.has(month.toLowerCase());
  const maxDay = DAYS_IN_MONTH[MONTH_NAMES.indexOf(month.toLowerCase())];
  const readings: Array<{ text: string; wordCount: number }> = [];

  // "april two thousand twenty six" is a month and year, not April 2.
  const monthYear = readLeadingYear(words);
  if (monthYear) {
    readings.push({ text: `${monthName} ${monthYear.year}`, wordCount: monthYear.length });
  }

  for (const dayLength of [2, 1]) {
    if (lowerWords.length < dayLength || lowerWords[dayLength - 1] === "and") continue;
    const dayWords = lowerWords.slice(0, dayLength);
    const day = parseSpokenDay(dayWords);
    if (!day) continue;
    // "april thirty first" isn't a real date, and April 30 would put the wrong one in the note.
    if (day.value > maxDay) return match;

    const year = readLeadingYear(words.slice(dayLength));
    const date = `${monthName} ${day.value}`;
    const isClearDay =
      day.isOrdinal && !(dayLength === 1 && AMBIGUOUS_MONTH_DAY_WORDS.has(dayWords[0]));

    if (year) {
      readings.push({ text: `${date}, ${year.year}`, wordCount: dayLength + year.length });
    } else if (!isAmbiguousMonth || isClearDay) {
      readings.push({ text: date, wordCount: dayLength });
    }
  }

  // The reading that uses the most spoken words wins, so "april nineteen twenty twenty six"
  // is April 19, 2026 rather than April 1920 followed by a stray "26".
  const best = readings.reduce<(typeof readings)[number] | null>(
    (currentBest, reading) =>
      !currentBest || reading.wordCount > currentBest.wordCount ? reading : currentBest,
    null
  );
  return best ? joinWords(best.text, ...words.slice(best.wordCount)) : match;
};

// Finishes a date whose year arrives in the next result ("since April 29" then "twenty twenty six").
const joinYearToPreviousMonthDate = (value: string, textBefore: string): string => {
  if (!TRAILING_MONTH_DATE_PATTERN.test(textBefore)) return value;
  const yearRun = value.match(LEADING_YEAR_RUN_PATTERN)?.[0];
  if (!yearRun) return value;
  const runWords = yearRun.split(/[\s-]+/);
  const year = readLeadingYear(runWords);
  if (!year) return value;
  return `, ${joinWords(String(year.year), ...runWords.slice(year.length))}${value.slice(yearRun.length)}`;
};

const parseWholeNumber = (tokens: string[]): number | null => {
  if (tokens.some(isScaleWord)) return parseCardinal(tokens);
  const groups = readGroups(tokens);
  return groups?.length === 1 ? groups[0] : null;
};

// "zero point five three" -> "0.53". A run like "eighteen twenty point five" stays as spoken.
// "and" joins a whole number only after a scale word ("one hundred and five point three");
// otherwise it separates numbers ("two and five point three" -> "two and 5.3").
const convertDecimal = (match: string, wholeWords: string, fractionWords: string): string => {
  const words = wholeWords.split(/[\s-]+/).filter(Boolean);
  const lowerWords = words.map((word) => word.toLowerCase());
  const lastSeparatingAnd = lowerWords.reduce(
    (lastIndex, word, index) =>
      word === "and" && !isScaleWord(lowerWords[index - 1] ?? "") ? index : lastIndex,
    -1
  );
  const whole = parseWholeNumber(toNumberTokens(words.slice(lastSeparatingAnd + 1).join(" ")));
  if (whole === null) return match;

  const fraction = fractionWords
    .toLowerCase()
    .split(/[\s-]+/)
    .map((word) => (word === "oh" ? 0 : getSingleDigitValue(word)))
    .join("");
  return joinWords(...words.slice(0, lastSeparatingAnd + 1), `${whole}.${fraction}`);
};

// Plain mode only: Smart Format already writes these as digits in formatted mode.
const normalizePlainClinicalNumbers = (value: string, textBefore: string): string =>
  joinYearToPreviousMonthDate(value, textBefore)
    .replace(A1C_PATTERN, "A1c")
    .replace(MONTH_DATE_PATTERN, convertMonthDate)
    .replace(DECIMAL_PATTERN, convertDecimal);

export interface PhoneNumberContinuationResult {
  transcript: string;
  /** The transcript continues the previous digits, so it's inserted without a leading space. */
  joinsPrevious: boolean;
}

const LEADING_DIGIT_WORDS_PATTERN = new RegExp(
  `^((?:${SINGLE_DIGIT_WORD_SOURCE})(?:[\\s-]+(?:${SINGLE_DIGIT_WORD_SOURCE}))*)\\b`,
  "i"
);

/** Continues a phone number that Deepgram split across pieces ("phone number is 555" + "one two"). */
export const normalizePhoneNumberContinuation = (
  valueBeforeInsertion: string,
  transcript: string
): PhoneNumberContinuationResult => {
  const unchanged = { transcript, joinsPrevious: false };
  const phoneContextMatch = valueBeforeInsertion.match(PHONE_NUMBER_CONTEXT_PATTERN);
  if (!phoneContextMatch) return unchanged;

  const leadingMatch = transcript.match(LEADING_DIGIT_WORDS_PATTERN);
  if (!leadingMatch) return unchanged;

  const digitWords = leadingMatch[1].split(/[\s-]+/).filter(Boolean);
  const remainingDigitCount = PHONE_NUMBER_DIGIT_COUNT - phoneContextMatch[1].length;
  // A lone digit word that doesn't finish the number is more likely a new sentence ("Six patients").
  if (digitWords.length < 2 && digitWords.length < remainingDigitCount) return unchanged;

  const joinedWords = digitWords.slice(0, remainingDigitCount);
  const leftoverWords = digitWords.slice(remainingDigitCount);
  const rest = transcript.slice(leadingMatch[1].length).trim();
  return {
    transcript: [toDigitString(joinedWords.join(" ")), ...leftoverWords, rest]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+([.,!?;:])/g, "$1"),
    joinsPrevious: true,
  };
};

export const normalizeSpokenDictation = (
  value: string,
  { mode, textBefore = "" }: NormalizeSpokenDictationOptions
): string => {
  let nextValue = value.trim();
  if (!nextValue) return "";

  const includesExplicitPeriodCommand = Array.from(nextValue.matchAll(PERIOD_COMMAND_PATTERN)).some(
    (match) => isPeriodCommandAt(match[0], match.index ?? 0, nextValue)
  );

  const commands =
    mode === "formatted"
      ? [...PUNCTUATION_COMMANDS, ...EXPLICIT_COMMANDS]
      : [...PUNCTUATION_COMMANDS, ...EXPLICIT_COMMANDS, ...BARE_COMMANDS];
  commands.forEach(([pattern, replacement]) => {
    nextValue =
      typeof replacement === "string"
        ? nextValue.replace(pattern, replacement)
        : nextValue.replace(pattern, replacement);
  });

  nextValue = nextValue.replace(PUNCTUATION_NEXT_TO_COMMAND_PATTERN, " $1 ");
  nextValue = nextValue
    .replace(
      LOWERCASE_AFTER_SPOKEN_PERIOD_PATTERN,
      (_match, periodAndSpace: string, letter: string) => `${periodAndSpace}${letter.toUpperCase()}`
    )
    .replace(new RegExp(PERIOD_TOKEN, "g"), ".")
    .replace(new RegExp(COMMA_TOKEN, "g"), ",");

  let cleanedValue = nextValue
    .replace(/\s+([.,!?;:%)\]}])/g, "$1")
    .replace(/([([{])\s+/g, "$1")
    .replace(/\s*\/\s*/g, "/")
    .replace(/(\w)\s*-\s*(\w)/g, "$1-$2")
    .replace(/"\s+([^"]*?)\s+"/g, '"$1"')
    .replace(new RegExp(`\\s*${NEWLINE_TOKEN}\\s*`, "g"), "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(SPACING_INSIDE_OPEN_QUOTE_PATTERN, "$1")
    .replace(SPACING_INSIDE_CLOSE_QUOTE_PATTERN, "$1")
    .replace(QUOTE_TOKEN_PATTERN, '"');

  if (mode === "plain") cleanedValue = normalizePlainClinicalNumbers(cleanedValue, textBefore);
  cleanedValue = normalizeSpokenNumbers(
    normalizeDictatedDates(cleanedValue, textBefore, { allowShortYear: mode === "plain" }),
    mode,
    textBefore
  );

  if (mode === "plain" && !includesExplicitPeriodCommand) {
    cleanedValue = cleanedValue.replace(/(^|[^.])\.+(?=\s*$)/g, "$1");
  }

  cleanedValue = cleanedValue.replace(/^[ \t]+|[ \t]+$/g, "");

  return collapseRepeatedCommandOutput(cleanedValue);
};

export const isDictationPunctuationOnly = (value: string): boolean => {
  const trimmedValue = value.trim();
  return (
    trimmedValue.length > 0 &&
    Array.from(trimmedValue).every((character) =>
      DICTATION_PUNCTUATION_CHARACTERS.includes(character)
    )
  );
};

export const isDictationLineBreakOnly = (value: string): boolean =>
  value.replace(/\n/g, "").length === 0 && value.includes("\n");

export const shouldCapitalizeNextDictationWord = (valueBeforeInsertion: string): boolean =>
  /(^|[.!?]\s*)$/.test(valueBeforeInsertion.trimEnd());

export const capitalizeFirstDictationWord = (value: string): string =>
  value.replace(/([A-Za-z])/, (match) => match.toUpperCase());
